
import { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { BarChart2, TrendingUp, Activity, DollarSign } from "lucide-react";
import AnalysisTab from "./AnalysisTab";
import PredictionTab from "./PredictionTab";
import Trading from "./Trading";
import DataTable from "./DataTable";

interface TabsViewProps {
  children?: React.ReactNode;
  className?: string;
  defaultValue?: string;
  onTabChange?: (value: string) => void;
  activeTab?: string;
}

const TabsView = ({ 
  children, 
  className, 
  defaultValue = "dashboard",
  onTabChange,
  activeTab
}: TabsViewProps) => {
  const [currentTab, setCurrentTab] = useState(activeTab || defaultValue);

  const handleTabChange = (value: string) => {
    setCurrentTab(value);
    if (onTabChange) {
      onTabChange(value);
    }
  };

  useEffect(() => {
    // Update active tab if it changes externally (e.g., from header)
    if (activeTab && activeTab !== currentTab) {
      setCurrentTab(activeTab);
    }
  }, [activeTab]);

  return (
    <Tabs 
      defaultValue={defaultValue} 
      value={currentTab}
      onValueChange={handleTabChange}
      className={cn("w-full", className)}
    >
      <TabsList className="grid grid-cols-4 mb-8">
        <TabsTrigger value="dashboard" className="flex items-center gap-2">
          <BarChart2 size={16} />
          <span className="hidden sm:inline-block">Dashboard</span>
        </TabsTrigger>
        
        <TabsTrigger value="analysis" className="flex items-center gap-2">
          <Activity size={16} />
          <span className="hidden sm:inline-block">Analysis</span>
        </TabsTrigger>
        
        <TabsTrigger value="prediction" className="flex items-center gap-2">
          <TrendingUp size={16} />
          <span className="hidden sm:inline-block">Prediction</span>
        </TabsTrigger>
        
        <TabsTrigger value="trading" className="flex items-center gap-2">
          <DollarSign size={16} />
          <span className="hidden sm:inline-block">Trading</span>
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="dashboard">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-card rounded-xl border p-6">
            <h2 className="text-2xl font-semibold mb-4">Dashboard Overview</h2>
            <p className="text-muted-foreground">
              Welcome to LakshBot, your AI-powered financial analysis and trading platform. 
              Use the tabs above to navigate between different features.
            </p>
            <ul className="mt-4 space-y-2">
              <li className="flex items-center">
                <Activity className="h-5 w-5 text-primary mr-2" />
                <span>Analysis - Technical analysis with correlation metrics</span>
              </li>
              <li className="flex items-center">
                <TrendingUp className="h-5 w-5 text-primary mr-2" />
                <span>Prediction - LSTM and reinforcement learning models</span>
              </li>
              <li className="flex items-center">
                <DollarSign className="h-5 w-5 text-primary mr-2" />
                <span>Trading - Connect to IBKR and execute trades</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-card rounded-xl border p-6">
            <h2 className="text-xl font-semibold mb-4">Recent Performance</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">S&P 500</span>
                <span className="text-sm text-green-600">+0.76%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">DJIA</span>
                <span className="text-sm text-green-600">+0.82%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">NASDAQ</span>
                <span className="text-sm text-green-600">+1.12%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Model Accuracy</span>
                <span className="text-sm font-bold">97.95%</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          <h2 className="text-xl font-semibold mb-4">Latest Backtesting Results</h2>
          {children}
        </div>
      </TabsContent>
      
      <TabsContent value="analysis">
        <AnalysisTab />
      </TabsContent>
      
      <TabsContent value="prediction">
        <PredictionTab />
      </TabsContent>
      
      <TabsContent value="trading">
        <Trading />
      </TabsContent>
    </Tabs>
  );
};

export default TabsView;
